import { FormHeader } from "app/components/atoms/layout";
import Table from "app/components/atoms/table";
import Typography from "app/components/atoms/typography";
import { InputMolecule } from "app/components/molecules/form";
import ImageWithPreview from "app/components/molecules/imageWithPreview";
import { useApplicationModals } from "app/common/hooks";
import { useCollector } from "app/integration/common/hooks";
import AppModals from "app/modals";
import { IconButton, ServerSidePaginationButtons } from "app/components/atoms/button";
import { SelectMolecule } from "app/components/molecules/select";
import { EyeIcon } from "@heroicons/react/24/outline";
import MainLayout from "app/layouts/main";
import { useEffect, useMemo, useState } from "react";
import { isUniqueBarcode } from "app/common/product";

/*
 * Rincian unit per gudang disembunyikan sementara (19 Agu 2026).
 *
 * Modal ini membaca `product_codes.warehouse_id`, dan kolom itu TIDAK ikut
 * diperbarui saat transfer gudang dilakukan per qty: ledger dan
 * `product_variant.qty` berpindah, tapi baris unitnya tetap tercap di gudang
 * asal. Akibatnya modal melaporkan semua unit di Gudang Utama padahal kolom
 * laporannya — yang membaca ledger — sudah benar. Contohnya varian 364
 * (26LE-001): laporan Utama 10 / Dev 15, modal 27 unit semuanya di Utama.
 *
 * Angka di tabel tetap benar, jadi yang perlu disembunyikan hanya rinciannya.
 *
 * Cara menyalakan kembali: setelah (a) `WarehouseTransferService.addDetail`
 * mewajibkan varian unik dipindah per unit — guard-nya sudah ada di sisi POS
 * di `PosWarehouseTransferService.addDetail` tapi belum di sisi ERP — dan
 * (b) baris `product_codes` yang salah cap diperbaiki, ubah nilai ini ke true.
 * Kode tombol dan modalnya sengaja dibiarkan utuh supaya cukup satu baris.
 */
const SHOW_UNIT_DETAIL = false;

const breadcrumbItems = [
  { label: "Reports", href: "#" },
  { label: "Stok per Gudang", href: "#" },
];

/**
 * Laporan stok tersedia per item di SELURUH gudang.
 *
 * Kolom gudang dibangun DINAMIS dari `warehouses` yang dikirim backend, jadi menambah
 * gudang baru tidak menuntut perubahan kode di sini.
 *
 * Catatan otoritas stok — penting saat membaca angkanya: "Gudang Utama" adalah gudang
 * VIRTUAL yang otoritasnya `product_variant.qty`, sementara gudang outlet dihitung dari
 * ledger `stock_movements`. Keduanya dijumlah backend; jangan menghitung ulang di sini
 * dengan sumber lain.
 */
function StockWarehouse() {
  const { openModal, setPayloads } = useApplicationModals();
  // Kotak pencarian dipisah dari parameter permintaan: ia berubah tiap ketikan,
  // sementara permintaan hanya boleh berubah saat ambang 3 karakter terlewati.
  const [search, setSearch] = useState("");
  const [categories] = useCollector({ modelName: "category" });

  /**
   * SATU objek untuk seluruh parameter permintaan — bukan empat useState terpisah.
   *
   * Bentuk lamanya membuat perubahan filter memicu DUA permintaan: effect pertama
   * berjalan dengan `page` yang masih lama (mis. 7), lalu effect kedua menyetel
   * page ke 1 dan memicu permintaan lagi. `useCollector` tidak membatalkan
   * permintaan sebelumnya, jadi respons halaman 7 yang datang belakangan bisa
   * menimpa respons halaman 1 — tabel menampilkan data halaman 7 sementara
   * kontrolnya berkata halaman 1.
   *
   * Dengan satu objek, penyetelan filter dan pengembalian `page` ke 1 terjadi
   * dalam SATU pembaruan state, jadi hanya ada satu permintaan.
   */
  const [params, setParams] = useState({
    page: 1,
    limit: 25,
    search: "",
    category_id: "",
    only_available: "true",
  });

  /** Mengubah filter SELALU mengembalikan halaman ke 1, dalam satu pembaruan. */
  const setFilter = (patch) => setParams((prev) => ({ ...prev, ...patch, page: 1 }));

  const [report, , loader, collect] = useCollector({
    modelName: "report",
    methodName: "stockByWarehouse",
    collectUponInstantiation: false,
  });

  // Ambang 3 karakter berlaku UNTUK PENCARIAN SAJA.
  //
  // Dulu penjagaannya `return` dari seluruh effect, sehingga saat kotak cari
  // berisi 1–2 karakter, mengganti kategori pun tidak mengirim permintaan apa
  // pun — tabel tetap menampilkan hasil lama dan filternya tampak tidak
  // berfungsi. Sekarang kata kunci di bawah ambang diperlakukan sebagai "tidak
  // ada pencarian", dan karena nilainya jadi sama dengan sebelumnya, mengetik
  // 1–2 karakter tetap tidak memicu kueri agregat baru.
  useEffect(() => {
    const kw = search.trim();
    const efektif = kw.length >= 3 ? kw : "";
    setParams((prev) => (prev.search === efektif ? prev : { ...prev, search: efektif, page: 1 }));
  }, [search]);

  useEffect(() => {
    collect(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // `useCollector` memulai state-nya sebagai [] dan mengembalikannya ke [] saat gagal,
  // jadi bentuk objeknya TIDAK boleh diasumsikan ada.
  const warehouses = useMemo(() => report?.warehouses ?? [], [report]);
  const summary = useMemo(() => report?.summary ?? [], [report]);
  const items = useMemo(() => (Array.isArray(report?.items) ? report.items : []), [report]);

  const paginationProps = useMemo(() => report?.paginationInformation ?? null, [report]);

  const grandTotal = useMemo(
    () => summary.reduce((sum, s) => sum + Number(s.total_qty ?? 0), 0),
    [summary]
  );

  // Stok satu item di satu gudang. Untuk Gudang Utama (`warehouse_id` null) angkanya
  // dari `stock_utama`; sisanya dari rincian `outlets`.
  const qtyAt = (item, warehouseId) => {
    if (warehouseId === null || warehouseId === undefined) {
      return Number(item?.stock_utama ?? 0);
    }
    const found = (item?.outlets ?? []).find((o) => o.warehouse_id === warehouseId);
    return Number(found?.qty ?? 0);
  };

  return (
    <MainLayout
      activeSidebarNavigation="report"
      activeSidebarChild="stock-warehouse"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Stok per Gudang"
    >
      {/* Kartu ringkasan — dihitung backend atas SELURUH katalog, jadi angkanya tidak
          berubah saat pencarian diketik. Total stok gudang tidak boleh bergantung
          pada filter tampilan. */}
      <AppModals items={["productCodes.view"]} onSuccess={{ "productCodes.view": () => {} }} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {summary.map((s) => (
          <div
            key={s.warehouse_id ?? "utama"}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm"
          >
            <p className="text-sm text-gray-500 dark:text-gray-400">{s.warehouse_name}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              <Typography.Currency fontSize="2xl" type="number" number={s.total_qty} />
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {s.item_count} item tersedia
            </p>
          </div>
        ))}
        <div className="bg-blue-50 dark:bg-blue-900 border-l-4 border-blue-500 rounded-lg p-4 shadow-sm">
          <p className="text-sm text-blue-600 dark:text-blue-300">Total Semua Gudang</p>
          <p className="text-2xl font-bold text-blue-700 dark:text-white">
            <Typography.Currency fontSize="2xl" type="number" number={grandTotal} />
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-300">
            {items.length} item ditampilkan
          </p>
        </div>
      </div>

      <Table.Container>
        <Table.UtilitiesWrapper>
          <Table.SearchBarWrapper>
            <FormHeader appearance="none">
              <InputMolecule testId="report-stock-warehouse-cari-produk-input"
                label="Cari Produk"
                placeholder="Nama atau kode produk"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </FormHeader>
            <FormHeader appearance="none">
              <SelectMolecule testId="report-stock-warehouse-kategori-select"
                label="Kategori"
                value={params.category_id}
                options={[
                  { value: "", label: "Semua kategori" },
                  ...(categories ?? []).map((c) => ({ value: c.id, label: c.name })),
                ]}
                onChange={(e) => setFilter({ category_id: e.target.value })}
              />
            </FormHeader>
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 ml-2">
              <input data-testid="report-stock-warehouse-checkbox-input"
                type="checkbox"
                checked={params.only_available === "true"}
                onChange={(e) => setFilter({ only_available: e.target.checked ? "true" : "false" })}
                className="w-4 h-4 cursor-pointer"
              />
              Hanya yang tersedia
            </label>
          </Table.SearchBarWrapper>
        </Table.UtilitiesWrapper>
        <Table.Wrapper asIndex={false}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading>Produk</Table.Heading>
              <Table.Heading>Warna</Table.Heading>
              <Table.Heading>Size</Table.Heading>
              <Table.Heading>Barcode</Table.Heading>
              {warehouses.map((w) => (
                <Table.Heading key={w.warehouse_id ?? "utama"} align="right">
                  {w.warehouse_name}
                </Table.Heading>
              ))}
              <Table.Heading align="right">Total</Table.Heading>
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {items.length === 0 && !loader?.state ? (
              <Table.BodyRow>
                {/* 5 kolom statis (Produk, Warna, Size, Barcode, Total) + kolom gudang dinamis */}
                <Table.Cell colSpan={warehouses.length + 5}>
                  <span className="italic text-gray-400">Belum ada data</span>
                </Table.Cell>
              </Table.BodyRow>
            ) : (
              items.map((item) => (
                <Table.BodyRow key={item.product_variant_id}>
                  <Table.Cell>
                    <div className="flex gap-2 items-center">
                      {item?.image && (
                        <ImageWithPreview
                          size="lg"
                          src={`${process.env.REACT_APP_AWS_LINK_URL}${item.image}`}
                        />
                      )}
                      <div className="flex flex-col">
                        <span>{item?.product_name}</span>
                        <span className="text-xs text-gray-500">{item?.product_code}</span>
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell>{item?.color_name || "-"}</Table.Cell>
                  <Table.Cell>{item?.size_name || "-"}</Table.Cell>
                  <Table.Cell>
                    {isUniqueBarcode(item?.is_unique_barcode) ? (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 whitespace-nowrap">
                        Unik / pcs
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 whitespace-nowrap">
                        Biasa / qty
                      </span>
                    )}
                  </Table.Cell>
                  {warehouses.map((w) => (
                    <Table.Cell key={w.warehouse_id ?? "utama"} align="right">
                      <Typography.Currency
                        fontSize="base"
                        type="number"
                        number={qtyAt(item, w.warehouse_id)}
                      />
                    </Table.Cell>
                  ))}
                  <Table.Cell align="right">
                    <div className="flex items-center justify-end gap-2">
                      <span className="font-semibold">
                        <Typography.Currency
                          fontSize="base"
                          type="number"
                          number={item?.total_qty}
                        />
                      </span>
                      {/*
                        Tombol rincian HANYA untuk barang unik. Barang per-qty tidak
                        punya baris `product_codes` sama sekali, jadi tombolnya akan
                        selalu membuka daftar kosong — lebih baik tidak ada daripada
                        menjanjikan rincian yang tidak pernah ada.

                        Dimatikan sementara lewat SHOW_UNIT_DETAIL — lihat alasannya
                        di deklarasi konstantanya di atas.
                      */}
                      {SHOW_UNIT_DETAIL && isUniqueBarcode(item?.is_unique_barcode) && (
                        <IconButton testId="report-stock-warehouse-eye-button"
                          icon={EyeIcon}
                          title="Lihat unit barcode per gudang"
                          onClick={() => {
                            setPayloads("productCodes.view", {
                              product_variant_id: item.product_variant_id,
                              product_name: item.product_name,
                              // Nama gudang dikirim dari sini: respons product-code
                              // hanya membawa `warehouse_id`, dan daftar gudang sudah
                              // ada di halaman ini dari endpoint laporannya.
                              warehouses,
                            });
                            openModal("productCodes.view");
                          }}
                        />
                      )}
                    </div>
                  </Table.Cell>
                </Table.BodyRow>
              ))
            )}
          </Table.Body>
        </Table.Wrapper>
        {/*
          Paginasi server-side. Dulu kueri laporan memakai `LIMIT 500` keras tanpa
          OFFSET: hasil terpotong TANPA pemberitahuan, dan begitu katalog melewati
          500 varian sisanya hilang dari laporan tanpa jejak di layar.
        */}
        <Table.PaginationWrapper>
          <ServerSidePaginationButtons
            paginationProps={paginationProps}
            params={params}
            setParams={setParams}
          />
        </Table.PaginationWrapper>
      </Table.Container>
    </MainLayout>
  );
}

export default StockWarehouse;
